import React, { useEffect, useMemo, useState } from "react";
import OptimizedChart from "./OptimizedChart";
import Papa from "papaparse";
import { fmtNumber, smartCrumbChainLabel } from "../utils/helpers";

export default function ExecutiveReport({ areaType, selectedName, latestByMetro, chartData, forecastByName, allDates, crumbsByName, onClose }) {
  const [priceFallback, setPriceFallback] = useState([]);
  const [otherFallback, setOtherFallback] = useState({ Days: [], Inventory: [], Rent: [] });
  const [dataLoadError, setDataLoadError] = useState(null);

  const latest = useMemo(() => {
    if (!selectedName) return null;
    return latestByMetro?.[selectedName] || null;
  }, [selectedName, latestByMetro]);

  const priceSeries = useMemo(() => {
    // Prefer provided chartData if available and in the expected shape
    if (Array.isArray(chartData) && chartData.length) {
      return chartData;
    }

    // Fallback: derive a Price series from allDates
    if (Array.isArray(allDates) && allDates.length && selectedName) {
      const rows = (allDates || []).filter(r => String(r.Metro) === String(selectedName));
      const series = rows
        .slice()
        .sort((a, b) => String(a.Date).localeCompare(String(b.Date)))
        .map(d => ({ date: d.Date, price: Number(d.Price) }))
        .filter(p => Number.isFinite(p.price));
      return series;
    }

    return [];
  }, [chartData, allDates, selectedName]);

  // Build derived series from allDates for non-Price metrics
  const derivedSeriesByMetric = useMemo(() => {
    const make = (metric) => {
      if (!Array.isArray(allDates) || !allDates.length || !selectedName) return [];
      const rows = (allDates || []).filter(r => String(r.Metro) === String(selectedName));
      return rows
        .slice()
        .sort((a, b) => String(a.Date).localeCompare(String(b.Date)))
        .map(d => ({ date: d.Date, price: Number(d[metric]) }))
        .filter(p => Number.isFinite(p.price));
    };
    return {
      Days: make('Days'),
      Inventory: make('Inventory'),
      Rent: make('Rent')
    };
  }, [allDates, selectedName]);

  // Unified series for delta calculations (prefer derived, fall back to CSV-loaded series)
  const deltaSeries = useMemo(() => {
    const normalize = (arr) => (arr || []).map(d => ({ date: String(d.date), value: Number(d.price ?? d.value) }));
    const fromAllDates = (metric) => normalize(derivedSeriesByMetric[metric] || []);
    const fromFallback = (metric) => {
      if (metric === 'Price') {
        const src = (priceSeries && priceSeries.length ? priceSeries : priceFallback) || [];
        return normalize(src);
      }
      return normalize((otherFallback[metric] || []));
    };
    const pick = (metric) => {
      const a = fromAllDates(metric);
      return a.length ? a : fromFallback(metric);
    };
    return {
      Price: pick('Price'),
      Rent: pick('Rent'),
      Inventory: pick('Inventory'),
      Days: pick('Days')
    };
  }, [derivedSeriesByMetric, priceSeries, priceFallback, otherFallback]);

  const computeDeltas = useMemo(() => {
    const subMonths = (yyyymm, m) => {
      const [y, mo] = yyyymm.split('-').map(Number);
      const d = new Date(y, (mo - 1) - m, 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    };
    const toMap = (series) => Object.fromEntries((series || []).map(p => [String(p.date).slice(0,7), Number(p.value)]));
    const pct = (now, prev) => (Number.isFinite(now) && Number.isFinite(prev) && prev !== 0) ? ((now/prev - 1) * 100) : null;
    const makeFor = (series, lowerBetter = false) => {
      if (!series || series.length < 2) return { one: null, three: null, twelve: null, latestDate: null };
      const latestDate = String(series[series.length - 1].date).slice(0,7);
      const map = toMap(series);
      const now = map[latestDate];
      const one = pct(now, map[subMonths(latestDate, 1)]);
      const three = pct(now, map[subMonths(latestDate, 3)]);
      const twelve = pct(now, map[subMonths(latestDate, 12)]);
      return { one, three, twelve, latestDate, lowerBetter };
    };
    return {
      Price: makeFor(deltaSeries.Price, false),
      Rent: makeFor(deltaSeries.Rent, false),
      Inventory: makeFor(deltaSeries.Inventory, true),
      Days: makeFor(deltaSeries.Days, true)
    };
  }, [deltaSeries]);

  // Dedicated loader for full historical series (Price, Rent, Inventory, Days)
  // Used only when the existing props (chartData/allDates) don't provide enough data.
  useEffect(() => {
    if (!selectedName || !areaType) return;

    const hasPrice = Array.isArray(priceSeries) && priceSeries.length > 0;
    const hasDays = Array.isArray(derivedSeriesByMetric.Days) && derivedSeriesByMetric.Days.length > 0;
    const hasInventory = Array.isArray(derivedSeriesByMetric.Inventory) && derivedSeriesByMetric.Inventory.length > 0;
    const hasRent = Array.isArray(derivedSeriesByMetric.Rent) && derivedSeriesByMetric.Rent.length > 0;

    // If we already have series for all metrics, nothing to do.
    if (hasPrice && hasDays && hasInventory && hasRent) return;

    let cancelled = false;

    const load = async () => {
      try {
        setDataLoadError(null);

        const levelDir = String(areaType).toLowerCase();
        const r2Key = `public/data/Merged_Full_Data/${levelDir}_5y_50k_merged.csv`;
        const { fetchR2CsvText } = await import("../utils/fetchR2Csv");
        const text = await fetchR2CsvText(r2Key);
        if (cancelled) return;

        let rows = [];
        Papa.parse(text, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: "greedy",
          complete: (result) => {
            rows = Array.isArray(result.data) ? result.data : [];
          },
        });

        if (cancelled || !rows.length) return;

        const regionId =
          latest?.RegionID ||
          latest?.regionId ||
          latest?.region_id ||
          crumbsByName?.[selectedName]?.RegionID ||
          crumbsByName?.[selectedName]?.regionId ||
          crumbsByName?.[selectedName]?.region_id ||
          null;

        const nameKeyMap = {
          State: "State",
          Metro: "Metro",
          County: "CountyName",
          City: "City",
          Zipcode: "Zipcode",
          Neighborhood: "Neighborhood",
        };
        const nameKey = nameKeyMap[areaType] || "Metro";

        let filtered = [];
        if (regionId != null) {
          filtered = rows.filter((r) => Number(r.RegionID) === Number(regionId));
        }
        if (!filtered.length && selectedName) {
          const target = String(selectedName).toLowerCase();
          filtered = rows.filter((r) => {
            const raw = r[nameKey];
            if (!raw) return false;
            const val = String(raw).toLowerCase();
            return (
              val === target ||
              val.includes(target) ||
              target.includes(val)
            );
          });
        }

        if (!filtered.length) {
          if (!cancelled) {
            console.warn("[EXECUTIVE_REPORT] No rows found for dedicated series loader", {
              areaType,
              selectedName,
              regionId,
            });
          }
          return;
        }

        const metricFieldMap = {
          Price: "Price",
          Rent: "Rent",
          Inventory: "Inventory",
          Days: "Days",
        };
        const makeSeries = (metric) =>
          filtered
            .map((r) => ({
              date: r.Date,
              price: Number(r[metricFieldMap[metric]]),
            }))
            .filter(
              (d) =>
                d.date &&
                Number.isFinite(d.price)
            )
            .sort((a, b) => String(a.date).localeCompare(String(b.date)));

        const seriesFromLoader = {
          Price: hasPrice ? priceSeries : makeSeries("Price"),
          Rent: hasRent ? derivedSeriesByMetric.Rent : makeSeries("Rent"),
          Inventory: hasInventory ? derivedSeriesByMetric.Inventory : makeSeries("Inventory"),
          Days: hasDays ? derivedSeriesByMetric.Days : makeSeries("Days"),
        };

        if (cancelled) return;

        // Store in local fallback state used by charts and delta calculations.
        if (!hasPrice && seriesFromLoader.Price.length) {
          setPriceFallback(seriesFromLoader.Price);
        }
        setOtherFallback((prev) => ({
          ...prev,
          Rent: hasRent && prev.Rent.length ? prev.Rent : seriesFromLoader.Rent,
          Inventory: hasInventory && prev.Inventory.length ? prev.Inventory : seriesFromLoader.Inventory,
          Days: hasDays && prev.Days.length ? prev.Days : seriesFromLoader.Days,
        }));
      } catch (err) {
        console.error("[EXECUTIVE_REPORT] Dedicated loader error", err);
        if (!cancelled) {
          setDataLoadError("Unable to load full historical series for this area.");
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    areaType,
    selectedName,
    latest,
    crumbsByName,
    priceSeries,
    derivedSeriesByMetric,
  ]);

  const pathLabel = useMemo(() => {
    // Use the latest data row instead of crumbsByName for more accurate path
    const crumb = latest || crumbsByName?.[selectedName];
    if (!crumb) return selectedName || '';
    return smartCrumbChainLabel(crumb, areaType) || (selectedName || '');
  }, [latest, crumbsByName, selectedName, areaType]);

  // Chart loading is now truly on-demand - only when user clicks chart buttons

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="er-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <style>{`
        @media print {
          @page { margin: 8mm 12mm 12mm 12mm; }
          body * { visibility: hidden !important; }
          .er-modal, .er-modal * { visibility: visible !important; }
          .er-overlay { position: static !important; background: transparent !important; display: block !important; align-items: initial !important; justify-content: initial !important; }
          .er-modal {
            width: 100% !important;
            max-height: none !important;
            overflow: visible !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            margin: 0 !important;
          }
          .print-break { break-after: page; page-break-after: always; }
          .er-actions { display: none !important; }
        }
      `}</style>
      <div className="er-modal" style={{ background: '#fff', width: 'min(1100px, 96vw)', maxHeight: '88vh', overflow: 'auto', borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid #eee' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 36, fontWeight: 800, color: '#111', marginBottom: 6 }}>AlgoreitAI Market Analysis</div>
            <h3 style={{ margin: 0 }}>Executives Report{selectedName ? ` – ${selectedName}` : ''}</h3>
          </div>
          <div className="er-actions">
            <button className="btn" onClick={handlePrint} style={{ marginRight: 8 }}>Print PDF</button>
            <button className="btn" onClick={onClose}>Close</button>
          </div>
        </div>

        <div style={{ padding: 16 }}>
          {/* Snapshot + Forecast */}
          {latest ? (
            <table className="table" style={{ marginBottom: 12 }}>
              <thead>
                <tr>
                  <th>Area</th>
                  <th>Price</th>
                  <th>Rent</th>
                  <th>Inventory</th>
                  <th>Days</th>
                  <th>Date</th>
                  <th>Fcst 1m</th>
                  <th>Fcst 3m</th>
                  <th>Fcst 12m</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{latest.Metro}</td>
                  <td>{latest.Price != null ? fmtNumber(latest.Price, 0) : '-'}</td>
                  <td>{latest.Rent != null ? fmtNumber(latest.Rent, 0) : '-'}</td>
                  <td>{latest.Inventory != null ? fmtNumber(latest.Inventory, 0) : '-'}</td>
                  <td>{latest.Days != null ? fmtNumber(latest.Days, 0) : '-'}</td>
                  <td>{latest.Date || ''}</td>
                  {(() => {
                    const fmtPct = (v) => (v != null && Number.isFinite(v) && v !== 0 ? `${Number(v).toFixed(1)}%` : '--');
                    return (
                      <>
                        <td>{fmtPct(latest?.f1m)}</td>
                        <td>{fmtPct(latest?.f3m)}</td>
                        <td>{fmtPct(latest?.f12m)}</td>
                      </>
                    );
                  })()}
                </tr>
              </tbody>
            </table>
          ) : (
            <div style={{ 
              padding: '30px 20px', 
              textAlign: 'center',
              background: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              {dataLoadError ? (
                <div style={{ color: '#d9534f' }}>
                  <h4 style={{ margin: '0 0 12px 0', color: '#c62828' }}>⚠️ Data Loading Error</h4>
                  <p style={{ margin: '8px 0', fontSize: '14px' }}>{dataLoadError}</p>
                  <div style={{ 
                    marginTop: '16px', 
                    padding: '12px',
                    background: '#fff3cd',
                    border: '1px solid #ffeeba',
                    borderRadius: '4px',
                    fontSize: '13px',
                    color: '#856404',
                    textAlign: 'left'
                  }}>
                    <strong>Troubleshooting steps:</strong>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      <li>Verify R2 environment variables are set (R2_BUCKET_NAME, R2_ACCOUNT_ID, R2_ACCESS_KEY_ID, R2_SECRET_ACCESS_KEY)</li>
                      <li>Ensure data files are uploaded to the R2 bucket</li>
                      <li>Check browser console for detailed error messages</li>
                      <li>Try selecting an area and waiting for data to load before opening this report</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div>
                  <h4 style={{ margin: '0 0 12px 0', color: '#666' }}>📊 No Data Available</h4>
                  <p style={{ margin: '8px 0', fontSize: '14px', color: '#666' }}>
                    {!selectedName 
                      ? 'Please select an area from the main dashboard to view data.'
                      : !latestByMetro || Object.keys(latestByMetro).length === 0
                        ? 'Data is still loading. Please wait a moment and try again.'
                        : `No data found for "${selectedName}". This area may not have data available.`
                    }
                  </p>
                  {selectedName && latestByMetro && Object.keys(latestByMetro).length > 0 && (
                    <div style={{ 
                      marginTop: '12px',
                      fontSize: '12px',
                      color: '#888'
                    }}>
                      <p>Available areas: {Object.keys(latestByMetro).slice(0, 5).join(', ')}
                      {Object.keys(latestByMetro).length > 5 ? ` and ${Object.keys(latestByMetro).length - 5} more...` : ''}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Path */}
          <div style={{ margin: '6px 0 14px', fontSize: 13 }}>
            <strong>Path:</strong>{' '}
            {(() => {
              // Use the latest data row instead of crumbsByName for more accurate path
              const crumb = latest || crumbsByName?.[selectedName];
              if (!crumb) return '(missing parent fields in data file)';
              return smartCrumbChainLabel(crumb, areaType) || '(missing parent fields in data file)';
            })()}
          </div>

          {/* Deltas summary (works with derived or fallback series) */}
          {(() => {
            const hasAny = ['Price','Rent','Inventory','Days'].some(k => (deltaSeries[k] || []).length > 1);
            if (!hasAny) return null;
            const renderDelta = (v, lowerBetter = false) => {
              if (v == null) return <span style={{ color: '#666' }}>-</span>;
              const isUp = v > 0;
              const improved = lowerBetter ? !isUp : isUp;
              const arrow = isUp ? '▲' : '▼';
              const color = improved ? '#118a00' : '#c22727';
              return (
                <span style={{ color }}>
                  {arrow} {Math.abs(v).toFixed(1)}%
                </span>
              );
            };
            return (
              <table className="table" style={{ marginBottom: 14 }}>
                <thead>
                  <tr>
                    <th colSpan={5}>Recent Changes</th>
                  </tr>
                  <tr>
                    <th>Metric</th>
                    <th>1m</th>
                    <th>3m</th>
                    <th>12m</th>
                    <th>Latest date</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { k: 'Price', label: 'Price', lowerBetter: false },
                    { k: 'Rent', label: 'Rent', lowerBetter: false },
                    { k: 'Inventory', label: 'Inventory', lowerBetter: true },
                    { k: 'Days', label: 'Days (lower better)', lowerBetter: true },
                  ].map(({k,label,lowerBetter}) => (
                    <tr key={k}>
                      <td>{label}</td>
                      <td>{renderDelta(computeDeltas[k].one, lowerBetter)}</td>
                      <td>{renderDelta(computeDeltas[k].three, lowerBetter)}</td>
                      <td>{renderDelta(computeDeltas[k].twelve, lowerBetter)}</td>
                      <td>{computeDeltas[k].latestDate || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })()}

          {/* Force page break before charts when printing to get two-page layout */}
          <div className="print-break" />

          {/* Charts */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 16,
              marginTop: 10,
              alignItems: 'stretch',
            }}
          >
            <section style={{ minHeight: 280, display: 'flex', flexDirection: 'column' }}>
              <h4 style={{ margin: '0 0 10px' }}>Price</h4>
              <div style={{ flex: 1, minHeight: 240, background: '#fafafa', border: '1px solid #eee', borderRadius: 6, padding: 8, overflow: 'hidden' }}>
                <OptimizedChart chartData={(priceSeries && priceSeries.length ? priceSeries : priceFallback)} selectedMetro={selectedName} tpByMetro={{}} isLoading={false} areaType={areaType} noMargins={true} />
              </div>
            </section>
            {/* Always render the 3 additional charts. Use derived series or fallback CSVs; show placeholder if still empty. */}
            {['Days','Inventory','Rent'].map((metric) => {
              const derived = derivedSeriesByMetric[metric] || [];
              const fallback = otherFallback[metric] || [];
              const series = derived.length ? derived : fallback;
              return (
                <section key={metric} style={{ minHeight: 280, display: 'flex', flexDirection: 'column' }}>
                  <h4 style={{ margin: '0 0 10px' }}>{metric}</h4>
                  <div style={{ flex: 1, minHeight: 240, background: '#fafafa', border: '1px solid #eee', borderRadius: 6, padding: 8, overflow: 'hidden' }}>
                    {series.length ? (
                      <OptimizedChart chartData={series} selectedMetro={selectedName} tpByMetro={{}} isLoading={false} areaType={areaType} noMargins={true} />
                    ) : (
                      <div style={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#777',
                        fontSize: 13
                      }}>
                        No data available for {metric} yet
                      </div>
                    )}
                  </div>
                </section>
              );
            })}
          </div>

          {/* Trends removed per request */}
        </div>
        <div style={{ padding: '0 16px 16px', fontSize: 12, color: '#666', textAlign: 'center' }}>
          <div>Raw Data Source: Zillow; Mls; US Census</div>
          <div style={{ marginTop: 8 }}>AlgoreitAI can make mistakes. User must verify the info.</div>
        </div>
      </div>
    </div>
  );
}


